/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  ShieldCheck, FileText, Check, X, Eye, RefreshCw, Wallet, Clock, CheckCircle2, XCircle,
} from 'lucide-react';
import { adminIseeQueue, adminGetAttestazione, adminVerifyIsee } from '@/lib/pricing';

/**
 * Sprint 9F — Admin panel to spot-check declared ISEE values against
 * uploaded attestazione PDFs. Approve/Reject with note.
 */

const STATUS_META = {
  pending: { Icon: Clock, cls: 'bg-amber-500/15 text-amber-600', label: 'In verifica' },
  verified: { Icon: CheckCircle2, cls: 'bg-emerald-500/15 text-emerald-600', label: 'Verificato' },
  rejected: { Icon: XCircle, cls: 'bg-red-500/15 text-red-600', label: 'Rigettato' },
};

const euro = (v) => (typeof v === 'number' ? `€ ${v.toFixed(2)}` : '—');

const AdminIseeVerificationPanel = () => {
  const [status, setStatus] = useState('pending');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);   // user object
  const [pdfData, setPdfData] = useState(null);     // {file_base64, mime_type, filename}
  const [pdfLoading, setPdfLoading] = useState(false);
  const [decisionLoading, setDecisionLoading] = useState(false);
  const [note, setNote] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminIseeQueue(status);
      setItems(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore caricamento coda');
    } finally { setLoading(false); }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const openReview = async (user) => {
    setSelected(user);
    setPdfData(null);
    setNote('');
    setPdfLoading(true);
    try {
      const data = await adminGetAttestazione(user.id);
      setPdfData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore caricamento PDF');
    } finally { setPdfLoading(false); }
  };

  const decide = async (action) => {
    if (!selected) return;
    if (action === 'reject' && !note.trim()) {
      return toast.error('Aggiungi una nota per il rigetto');
    }
    setDecisionLoading(true);
    try {
      await adminVerifyIsee(selected.id, action, note.trim() || null);
      toast.success(action === 'approve' ? 'ISEE approvato' : 'ISEE rigettato');
      setSelected(null);
      setPdfData(null);
      setNote('');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore decisione');
    } finally { setDecisionLoading(false); }
  };

  const counts = items.length;

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
      data-testid="admin-isee-panel">
      <Card className="rounded-3xl border-primary/25 overflow-hidden">
        <CardHeader className="bg-gradient-to-br from-primary/10 via-card to-card">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div>
              <CardTitle className="flex items-center gap-2 text-base">
                <Wallet className="h-5 w-5 text-primary" />
                Verifica ISEE (Sprint 9F)
              </CardTitle>
              <CardDescription>
                Coda utenti con attestazione caricata. Verifica il PDF e approva o rigetta lo sconto.
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" data-testid="admin-isee-count">{counts} utenti</Badge>
              <Button variant="outline" size="sm" onClick={load} data-testid="admin-isee-refresh">
                <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Aggiorna
              </Button>
            </div>
          </div>
          <Tabs value={status} onValueChange={setStatus} className="mt-3">
            <TabsList className="bg-secondary/60 rounded-full p-1">
              <TabsTrigger value="pending" className="rounded-full text-xs" data-testid="admin-isee-tab-pending">In verifica</TabsTrigger>
              <TabsTrigger value="verified" className="rounded-full text-xs" data-testid="admin-isee-tab-verified">Verificati</TabsTrigger>
              <TabsTrigger value="rejected" className="rounded-full text-xs" data-testid="admin-isee-tab-rejected">Rigettati</TabsTrigger>
              <TabsTrigger value="all" className="rounded-full text-xs" data-testid="admin-isee-tab-all">Tutti</TabsTrigger>
            </TabsList>
          </Tabs>
        </CardHeader>
        <CardContent className="pt-4">
          {loading && (
            <div className="space-y-2">
              <Skeleton className="h-16 rounded-2xl" />
              <Skeleton className="h-16 rounded-2xl" />
            </div>
          )}
          {!loading && items.length === 0 && (
            <div className="text-sm text-muted-foreground text-center py-8" data-testid="admin-isee-empty">
              Nessun utente in questa coda.
            </div>
          )}
          <div className="space-y-2">
            <AnimatePresence initial={false}>
              {items.map((u) => {
                const isee = u.pricing_isee || {};
                const vs = isee.verification_status || 'pending';
                const meta = STATUS_META[vs] || STATUS_META.pending;
                return (
                  <motion.div
                    key={u.id}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -4 }}
                    layout
                    className="flex items-center gap-3 rounded-2xl border bg-card p-3"
                    data-testid={`admin-isee-row-${u.id}`}
                  >
                    <div className="h-10 w-10 rounded-xl bg-primary/15 text-primary flex items-center justify-center flex-shrink-0">
                      <FileText className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-semibold text-sm truncate">{u.full_name || u.email || u.id}</div>
                        <Badge className={`${meta.cls} border-0 gap-1`}><meta.Icon className="h-3 w-3" /> {meta.label}</Badge>
                      </div>
                      <div className="text-xs text-muted-foreground truncate">{u.email}</div>
                      <div className="text-xs mt-0.5 flex flex-wrap gap-3">
                        <span>ISEE dichiarato: <b>{euro(isee.isee_value)}</b></span>
                        <span>Tier: <b>{isee.tier_label || '—'}</b></span>
                        <span>Sconto: <b>–{isee.discount_pct || 0}%</b></span>
                      </div>
                      {isee.attestazione?.filename && (
                        <div className="text-[11px] text-muted-foreground mt-0.5 truncate">
                          PDF: {isee.attestazione.filename} • caricato il {new Date(isee.attestazione.uploaded_at).toLocaleDateString('it')}
                        </div>
                      )}
                      {isee.verification_note && (
                        <div className="text-[11px] mt-0.5"><b>Nota:</b> {isee.verification_note}</div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Button size="sm" variant="outline" onClick={() => openReview(u)}
                        data-testid={`admin-isee-review-${u.id}`}>
                        <Eye className="h-3.5 w-3.5" /> Rivedi
                      </Button>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        </CardContent>
      </Card>

      {/* ---------- Review Dialog ---------- */}
      <Dialog open={!!selected} onOpenChange={(o) => { if (!o) { setSelected(null); setPdfData(null); setNote(''); } }}>
        <DialogContent className="max-w-4xl w-[95vw] max-h-[92vh] flex flex-col p-0 gap-0" data-testid="admin-isee-review-dialog">
          <DialogHeader className="p-5 pb-3 border-b flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-primary" />
              Verifica attestazione ISEE
            </DialogTitle>
            <DialogDescription>
              {selected?.full_name} — ISEE dichiarato <b>{euro(selected?.pricing_isee?.isee_value)}</b> — sconto attuale <b>–{selected?.pricing_isee?.discount_pct || 0}%</b>
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 min-h-0 grid md:grid-cols-[1fr_320px] gap-0 overflow-hidden">
            {/* PDF viewer */}
            <div className="bg-slate-950/95 min-h-[420px] flex items-center justify-center">
              {pdfLoading && <Skeleton className="h-full w-full" />}
              {!pdfLoading && pdfData?.file_base64 && (
                <iframe
                  title="attestazione-pdf"
                  src={pdfData.file_base64.startsWith('data:') ? pdfData.file_base64 : `data:${pdfData.mime_type || 'application/pdf'};base64,${pdfData.file_base64}`}
                  className="w-full h-full min-h-[420px] bg-white"
                  data-testid="admin-isee-pdf-iframe"
                />
              )}
              {!pdfLoading && !pdfData?.file_base64 && (
                <div className="text-white/70 text-sm p-6">Nessun PDF disponibile</div>
              )}
            </div>

            {/* Decision sidebar */}
            <div className="p-4 border-l overflow-y-auto flex flex-col gap-3">
              <div className="text-xs text-muted-foreground uppercase tracking-widest">Nota decisione</div>
              <Textarea
                placeholder="Note interne (obbligatorie in caso di rigetto). Es. 'ISEE incoerente con documento allegato'."
                value={note} onChange={(e) => setNote(e.target.value)}
                rows={5}
                data-testid="admin-isee-note"
              />
              <div className="text-xs text-muted-foreground leading-snug">
                <b>Approva</b>: marca l'ISEE come <i>verificato</i> — lo sconto attuale resta attivo.<br/>
                <b>Rigetta</b>: azzera lo sconto ISEE (mantiene il bundle famiglia). L'utente verrà notificato.
              </div>
              <DialogFooter className="mt-auto flex-row gap-2 sm:justify-stretch">
                <Button variant="outline" onClick={() => decide('reject')} disabled={decisionLoading}
                  className="flex-1 text-red-600 border-red-300 hover:bg-red-50 hover:text-red-700"
                  data-testid="admin-isee-reject-btn">
                  <X className="h-4 w-4" /> Rigetta
                </Button>
                <Button onClick={() => decide('approve')} disabled={decisionLoading}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  data-testid="admin-isee-approve-btn">
                  <Check className="h-4 w-4" /> Approva
                </Button>
              </DialogFooter>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
};

export default AdminIseeVerificationPanel;
